import { format, parseISO } from 'date-fns';
import { CronExpressionParser } from 'cron-parser';
import { PRETTY_DATE_FORMAT } from '@Components/ModalEditWidget/modal-edit-constants';
import { QueryRow } from './query-exporter-constants';

// A freshly-inserted row is keyed by a client-generated negative placeholder id (see
// applyQueryRowChanges) until its insert POST resolves and the refetch replaces it with the
// server's real id. Saving an edit -- or a delete -- against that placeholder would ship an
// `update`/`delete` the backend can't match to any row, silently dropping the change once the
// refetch overwrites it -- so block both until the real id comes back instead.
export const isPendingInsert = (id: number | undefined): boolean => id !== undefined && id < 0;

export const checkQueryRowIsValid = (
  data: Partial<QueryRow>,
  handleInvalidMessage?: (message: string) => void
): boolean => {
  if (isPendingInsert(data.id)) {
    handleInvalidMessage?.('This query is still being created -- try again in a moment.');
    return false;
  }

  if (!data.name) {
    handleInvalidMessage?.('Name field cannot be empty');
    return false;
  }

  return true;
};

export const checkQueryRowCanDelete = (row: Partial<QueryRow>): boolean => !isPendingInsert(row.id);

export const isCronInvalid = (cron: string): boolean => {
  if (!cron) return true;
  if (cron === '-') return false;

  try {
    CronExpressionParser.parse(cron, { tz: 'ETC/UTC' });
    return false;
  } catch {
    return true;
  }
};

// Backend timestamps come back with full ISO precision (seconds, offset); trims that down to
// just date + time for display. Falls back to the raw string if it's not parseable ISO.
const formatTimestamp = (raw: string): string => {
  const parsed = parseISO(raw);
  return Number.isNaN(parsed.getTime()) ? raw : format(parsed, PRETTY_DATE_FORMAT);
};

// Bookkeeping fields that don't need their own editable modal field -- surfaced instead as
// stacked lines in the row editor's footer via ModalEditWidget's footerMetadata.
export const getQueryRowMetadataLines = (row: Partial<QueryRow>): string[] => {
  const lines: string[] = [];

  if (row.completed_status) {
    lines.push(`Status: ${row.completed_status}`);
  }
  if (row.last_executed) {
    lines.push(`Last executed: ${formatTimestamp(row.last_executed)}`);
  }
  if (row.last_completed) {
    lines.push(`Last completed: ${formatTimestamp(row.last_completed)}`);
  }
  if (row.last_modified) {
    const modified = formatTimestamp(row.last_modified);
    lines.push(`Last modified ${modified}${row.last_user ? ` by ${row.last_user}` : ''}`);
  }

  return lines;
};
